import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  useAcceptMyInvitation,
  useMyMemberships,
  useMyPendingInvitations,
} from '@/hooks/useClinics';
import { globalHref, safeNextPath, tenantHref } from '@/routing/tenant';
import { translateApiError } from '@/lib/apiError';
import { invitationRoleNames } from '@/lib/invitations';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Logo } from '@/components/synapse/Logo';
import { Icon } from '@/components/synapse/Icon';
import { InitialsAvatar } from '@/components/synapse/Avatar';
import { ThemeToggle } from '@/components/synapse/ThemeToggle';
import { LanguageToggle } from '@/components/synapse/LanguageToggle';
import { useEffect, useState } from 'react';
import type { ActiveClinic } from '@/types/api';

/**
 * Enter a clinic. Always a full page load: each clinic is its own URL namespace
 * and the router's `basename` is fixed at mount, so this crosses a boundary
 * `navigate()` cannot. It also guarantees a clean query cache, which is what
 * stops one clinic's rows appearing under another's header.
 */
function enterClinic(slug: string, next: string | null): void {
  window.location.replace(tenantHref(slug, next ?? '/'));
}

/**
 * The clinic picker — where a session goes when the app cannot tell which clinic
 * the user means.
 *
 * That covers two cases which look different to a user and are the same question
 * here: they belong to several clinics, or they belong to none. This is the only
 * screen that sees BOTH memberships and pending invitations, which is what lets
 * it tell "you have nowhere to go, let's create a clinic" apart from "you have
 * an invitation waiting" — a distinction the post-auth router cannot make
 * without fetching both itself.
 */
export function SelectClinicPage() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  // Attacker-controlled by the time we read it back — it round-trips through the
  // query string and ends up at `window.location`.
  const next = safeNextPath(searchParams.get('next'));

  const membershipsQuery = useMyMemberships();
  const invitationsQuery = useMyPendingInvitations();
  const acceptInvite = useAcceptMyInvitation();

  const memberships = membershipsQuery.data;
  const invitations = invitationsQuery.data;

  // One clinic and nothing to decide between — don't make them click a list of
  // one. (Reached directly by URL; the post-auth router sends single-clinic
  // users straight in without coming here at all.)
  //
  // In an effect rather than inline in the body: leaving the page is a side
  // effect, and React may render a component more than once before committing
  // it. `soleClinic` is null until both queries have settled, which is what stops
  // this firing off a half-loaded picture.
  //
  // Only ENTERABLE clinics count. A disabled one cannot be entered — the shell's
  // guard bounces straight back here — so shortcutting into it would loop
  // forever for anyone whose only clinic was switched off. The list below still
  // renders every membership; this is about where to go, not what to show.
  //
  // And the shortcut only fires when nothing is hidden by that filter: a member
  // of two clinics with one disabled must SEE this page, badge and all, rather
  // than be forwarded into the survivor as though the other never existed.
  // Same rule as `pickPostAuthDestination`, for the same reason.
  const enterable = memberships?.filter((m) => !m.disabled);
  const soleClinic =
    memberships &&
    enterable &&
    invitations &&
    memberships.length === 1 &&
    enterable.length === 1 &&
    invitations.length === 0
      ? enterable[0]!
      : null;
  const soleSlug = soleClinic?.slug ?? null;
  useEffect(() => {
    if (soleSlug) enterClinic(soleSlug, next);
  }, [soleSlug, next]);

  // Both must settle before deciding: acting on a half-loaded picture is how you
  // send someone who has an invitation waiting to the "create your first clinic"
  // wizard instead.
  if (!memberships || !invitations) return null;

  if (memberships.length === 0 && invitations.length === 0) {
    return <Navigate to="/create-dispensary" replace />;
  }

  // The effect above is navigating away; render nothing rather than flashing a
  // one-item list on the way out.
  if (soleClinic) return null;

  async function onAccept(invitationId: string) {
    setError(null);
    try {
      const membership = await acceptInvite.mutateAsync(invitationId);
      enterClinic(membership.slug, next);
    } catch (err) {
      // Most commonly a 409: someone added them to the clinic in the interval,
      // or another tab accepted first. Refetch so the stale card disappears.
      setError(translateApiError(err, { fallback: t('selectClinic.errAcceptFailed') }));
      void membershipsQuery.refetch();
      void invitationsQuery.refetch();
    }
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-background text-foreground">
      <header className="flex h-14 shrink-0 items-center gap-4 border-b border-line bg-paper px-4 md:px-6">
        <Logo size={22} />
        <div className="ml-auto flex items-center gap-2">
          <ThemeToggle />
          <LanguageToggle />
          <Button variant="ghost" size="sm" onClick={() => logout({ redirect: true })}>
            {t('common.signOut')}
          </Button>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-xl flex-1 flex-col gap-6 px-4 py-10 md:py-16">
        <div className="flex flex-col gap-1">
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">
            {t('selectClinic.title')}
          </h1>
          <p className="text-[13px] text-muted">
            {t('selectClinic.subtitle', { name: user?.name ?? '' })}
          </p>
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-[13px] text-danger"
          >
            {error}
          </div>
        )}

        {memberships.length > 0 && (
          <section className="flex flex-col gap-2">
            <h2 className="text-[12px] uppercase tracking-[0.04em] text-muted">
              {t('selectClinic.yourClinics')}
            </h2>
            {memberships.map((clinic) => (
              <ClinicCard key={clinic.id} clinic={clinic} onEnter={() => enterClinic(clinic.slug, next)} />
            ))}
          </section>
        )}

        {invitations.length > 0 && (
          <section className="flex flex-col gap-2">
            <h2 className="text-[12px] uppercase tracking-[0.04em] text-muted">
              {t('selectClinic.invitations')}
            </h2>
            {invitations.map((invite) => (
              <Card key={invite.id} className="flex items-center gap-3 p-3">
                <InitialsAvatar name={invite.clinicName} size={36} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[14px] font-medium">{invite.clinicName}</div>
                  <div className="truncate text-[12px] text-muted">
                    {invitationRoleNames(invite.roles)}
                  </div>
                </div>
                <Button
                  size="sm"
                  disabled={acceptInvite.isPending}
                  onClick={() => void onAccept(invite.id)}
                >
                  {t('selectClinic.acceptJoin')}
                </Button>
              </Card>
            ))}
          </section>
        )}

        <a
          href={globalHref('/create-dispensary')}
          className="flex items-center justify-center gap-2 rounded-md border border-dashed border-line px-3 py-3 text-[13px] text-muted transition-colors hover:border-ink hover:text-ink"
        >
          <Icon name="plus" size={16} />
          {t('selectClinic.createAnother')}
        </a>

        {/* The one way into the platform surface, on the one screen that is
            already about "where do I go" — nothing platform-shaped appears
            inside a clinic's own UI. An `<a>` with `globalHref` rather than a
            router link because the picker can be reached from inside a tenant
            basename, and that crossing has to be a hard navigation. */}
        {user?.isSuperAdmin && (
          <a
            href={globalHref('/admin')}
            className="flex items-center justify-center gap-2 text-[13px] text-muted transition-colors hover:text-ink"
          >
            {t('selectClinic.platformAdmin')}
          </a>
        )}
      </main>
    </div>
  );
}

/**
 * One clinic in the list.
 *
 * A disabled clinic is rendered as a non-interactive row rather than being
 * hidden: the member is still a member, and "my clinic vanished" is a far worse
 * thing to discover than "my clinic is switched off". It is not a `<button>` at
 * all, so there is nothing to click and nothing for a keyboard to focus — the
 * destination genuinely does not exist for them right now.
 */
function ClinicCard({ clinic, onEnter }: { clinic: ActiveClinic; onEnter: () => void }) {
  const { t } = useTranslation();
  const roleLabel = clinic.roles.map((r) => r.name).join(', ');
  const body = (
    <>
      <InitialsAvatar name={clinic.name} size={36} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="truncate text-[14px] font-medium">{clinic.name}</span>
          {clinic.disabled && (
            <Badge tone="danger" size="sm">
              {t('selectClinic.disabled')}
            </Badge>
          )}
        </div>
        <div className="truncate text-[12px] text-muted">
          {clinic.disabled ? t('selectClinic.disabledHint') : `/${clinic.slug}`}
          {!clinic.disabled && roleLabel && ` · ${roleLabel}`}
        </div>
      </div>
    </>
  );

  if (clinic.disabled) {
    return (
      <div
        aria-disabled="true"
        className="flex w-full items-center gap-3 rounded-md border border-line bg-paper p-3 text-left opacity-60"
      >
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onEnter}
      className="flex w-full items-center gap-3 rounded-md border border-line bg-paper p-3 text-left transition-colors hover:border-ink"
    >
      {body}
      <Icon name="chevron-right" size={16} className="shrink-0 text-muted" />
    </button>
  );
}
